import { FormFieldDefinition } from '@publishing-engine/domain/types/article';
import {
  ArticleTypeConfigField,
  FieldEditorSubmit,
  FieldFormValues,
  SourceFieldOption
} from './articleTypeConfig.types';

const DEPENDENT_VALIDATION_KINDS = new Set([
  'dependantStringField',
  'dependantCheckboxArray',
  'dependantCheckboxArrayWithText'
]);

const NON_FLATTENED_PARENT_KEYS = new Set(['allowedFileFormats', 'items']);
const EXCLUDED_VALIDATION_EDITOR_KEYS = new Set([
  'kind',
  'required',
  'dependantField',
  'dependentField'
]);
const NON_EDITABLE_VALIDATION_KEYS = new Set(['pattern', 'items']);

type JsonRecord = Record<string, unknown>;
type ValidationInputValue = string | boolean;
type ValidationFieldInputType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'array'
  | 'object';

type ValidationFieldDefinition = {
  key: string;
  inputType: ValidationFieldInputType;
  value: unknown;
};

const DEFAULT_ALLOWED_FILE_FORMAT_MAP: Record<string, string[]> = {
  'image/jpeg': ['.jpg'],
  'image/png': ['.png'],
  'video/mp4': ['.mp4'],
  'video/mpeg': ['.mpg'],
  'video/webm': ['.webm'],
  'video/x-msvideo': ['.avi'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx'
  ],
  'application/vnd.openxmlformats': ['.docx'],
  'application/x-tex': ['.tex'],
  'application/x-latex': ['.tex', '.latex'],
  'application/zip': ['.zip'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx'
  ],
  'text/plain': ['.txt'],
  'application/vnd.ms-excel': ['.xls']
};

const KINDS_WITH_ALLOWED_FILE_FORMATS = new Set([
  'file',
  'fileArray',
  'customArray'
]);

const VALIDATION_FIELDS_BY_KIND: Record<
  string,
  Array<{ key: string; inputType: ValidationFieldInputType }>
> = {
  string: [
    { key: 'minLength', inputType: 'number' },
    { key: 'maxLength', inputType: 'number' },
    { key: 'pattern', inputType: 'string' }
  ],
  nullString: [{ key: 'maxLength', inputType: 'number' }],
  bool: [{ key: 'value', inputType: 'boolean' }],
  file: [
    { key: 'maxSizeMB', inputType: 'number' },
    { key: 'allowedFileFormats', inputType: 'object' }
  ],
  fileArray: [
    { key: 'minItems', inputType: 'number' },
    { key: 'maxItems', inputType: 'number' },
    { key: 'maxSizeMB', inputType: 'number' },
    { key: 'allowedFileFormats', inputType: 'object' },
    { key: 'blockedFileFormats', inputType: 'object' }
  ],
  options: [{ key: 'options', inputType: 'array' }],
  stringArray: [
    { key: 'minCountOfKeywords', inputType: 'number' },
    { key: 'maxCountOfKeywords', inputType: 'number' },
    { key: 'minLength', inputType: 'number' },
    { key: 'maxLength', inputType: 'number' }
  ],
  dependantStringField: [
    { key: 'options', inputType: 'array' },
    { key: 'dependentOptions', inputType: 'array' },
    { key: 'maxLength', inputType: 'number' },
    { key: 'pattern', inputType: 'string' }
  ],
  dependantCheckboxArray: [
    { key: 'minCountOfKeywords', inputType: 'number' },
    { key: 'maxCountOfKeywords', inputType: 'number' }
  ],
  dependantCheckboxArrayWithText: [
    { key: 'minCountOfKeywords', inputType: 'number' },
    { key: 'maxCountOfKeywords', inputType: 'number' },
    { key: 'textMaxLength', inputType: 'number' }
  ],
  number: [
    { key: 'min', inputType: 'number' },
    { key: 'max', inputType: 'number' }
  ],
  integer: [
    { key: 'min', inputType: 'number' },
    { key: 'max', inputType: 'number' }
  ],
  date: [
    { key: 'min', inputType: 'string' },
    { key: 'max', inputType: 'string' }
  ],
  phoneNumber: [
    { key: 'minLength', inputType: 'number' },
    { key: 'maxLength', inputType: 'number' }
  ],
  password: [
    { key: 'minLength', inputType: 'number' },
    { key: 'maxLength', inputType: 'number' }
  ],
  customArray: [
    { key: 'minItems', inputType: 'number' },
    { key: 'maxItems', inputType: 'number' },
    { key: 'maxSizeMB', inputType: 'number' },
    { key: 'allowedFileFormats', inputType: 'object' },
    { key: 'items', inputType: 'object' }
  ],
  objectArray: [
    { key: 'minItems', inputType: 'number' },
    { key: 'maxItems', inputType: 'number' }
  ],
  reviewers: [
    { key: 'minItems', inputType: 'number' },
    { key: 'maxItems', inputType: 'number' }
  ],
  authors: [
    { key: 'minItems', inputType: 'number' },
    { key: 'orcidField', inputType: 'string' },
    { key: 'affiliationField', inputType: 'string' },
    { key: 'contributionsField', inputType: 'string' },
    { key: 'contributionDegreeField', inputType: 'string' }
  ],
  author: [
    { key: 'orcidRequired', inputType: 'boolean' },
    { key: 'affiliationRequired', inputType: 'boolean' }
  ],
  authorType: [
    { key: 'minLength', inputType: 'number' },
    { key: 'maxLength', inputType: 'number' }
  ],
  targetJournals: [
    { key: 'minItems', inputType: 'number' },
    { key: 'maxItems', inputType: 'number' }
  ]
};

const VALIDATION_KEY_INPUT_TYPE_OVERRIDES: Record<
  string,
  ValidationFieldInputType
> = {
  min: 'number',
  max: 'number',
  minLength: 'number',
  maxLength: 'number',
  minItems: 'number',
  maxItems: 'number',
  minCountOfKeywords: 'number',
  maxCountOfKeywords: 'number',
  maxSizeMB: 'number',
  textMaxLength: 'number',
  value: 'boolean',
  orcidRequired: 'boolean',
  affiliationRequired: 'boolean',
  options: 'array',
  dependentOptions: 'array',
  blockedFileFormats: 'object',
  items: 'object',
  allowedFileFormats: 'object'
};

const toRecord = (value: unknown): JsonRecord => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return value as JsonRecord;
};

const getStringValue = (value: unknown) => {
  if (value === undefined || value === null) return '';
  return String(value);
};

const getLabelHelpTextValue = ({
  labels,
  source
}: {
  labels: JsonRecord;
  source?: FormFieldDefinition;
}) => {
  const sourceLabels = toRecord(source?.params?.labels);
  const resolvedValue =
    labels.helpText ??
    labels.help_text ??
    labels.helptext ??
    sourceLabels.helpText ??
    sourceLabels.help_text ??
    sourceLabels.helptext;

  return getStringValue(resolvedValue).trim();
};

const normalizeFieldKey = (value: string) => value.trim().toLowerCase();

const toPositiveNumber = (value: unknown) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0
    ? parsedValue
    : undefined;
};

const isObject = (value: unknown) =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const getValidationKind = (validations: unknown) =>
  getStringValue(toRecord(validations).kind);

export const hasContentLabelKey = (labels: unknown) =>
  Object.hasOwnProperty.call(toRecord(labels), 'content');

export const shouldUseContentLabelMode = (labels: unknown) => {
  const labelsRecord = toRecord(labels);
  const hasContentKey = hasContentLabelKey(labelsRecord);
  if (!hasContentKey) return false;

  const contentValue = getStringValue(labelsRecord.content).trim();
  const mainValue = getStringValue(labelsRecord.main).trim();
  const subtitleValue = getStringValue(labelsRecord.subtitle).trim();
  const hasMainOrSubtitleValue = Boolean(mainValue || subtitleValue);

  // If content key exists but content is empty while title/subtitle has value,
  // keep title/subtitle mode (do not force content-only mode).
  if (!contentValue && hasMainOrSubtitleValue) {
    return false;
  }

  return true;
};

const getExternalDependencyPath = (source?: FormFieldDefinition) => {
  if (!source) return '';

  const params = toRecord((source as { params?: unknown }).params);
  const externalFieldDependency = toRecord(params.externalFieldDependency);
  return getStringValue(externalFieldDependency.path).trim();
};

export const getCaseDataMainPath = (source?: FormFieldDefinition) => {
  if (!source) return '';

  const caseDataFields = toRecord(
    (source as { caseDataFields?: unknown }).caseDataFields
  );
  return getStringValue(caseDataFields.main).trim();
};

const getDependentFieldFromCaseData = (source?: FormFieldDefinition) => {
  if (!source) return '';

  const caseDataFields = toRecord(
    (source as { caseDataFields?: unknown }).caseDataFields
  );
  const dependsOn = getStringValue(caseDataFields.dependsOn).toLowerCase();
  const hasDependentExists = dependsOn === 'exists' || dependsOn === 'exist';
  if (!hasDependentExists) return '';

  const mainPath = getCaseDataMainPath(source);
  if (!mainPath) return '';

  const mainFieldName = mainPath.split('.').filter(Boolean).pop() || '';
  return normalizeText(mainFieldName);
};

const normalizeFileFormatExtension = (extension: string) =>
  extension.trim().toLowerCase();

const toAllowedFileFormatMap = (value: unknown) => {
  const rawMap = toRecord(value);
  const nextMap: Record<string, string[]> = {};

  Object.entries(rawMap).forEach(([mimeType, extensions]) => {
    if (!Array.isArray(extensions)) return;

    const normalizedExtensions = extensions
      .map(ext => normalizeFileFormatExtension(String(ext)))
      .filter(Boolean);

    if (!normalizedExtensions.length) return;
    nextMap[mimeType] = Array.from(new Set(normalizedExtensions));
  });

  return nextMap;
};

const mergeAllowedFileFormatMaps = (
  ...maps: Array<Record<string, string[]>>
) => {
  const mergedMap: Record<string, string[]> = {};

  maps.forEach(map => {
    Object.entries(map).forEach(([mimeType, extensions]) => {
      if (!extensions.length) return;

      const previous = mergedMap[mimeType] || [];
      mergedMap[mimeType] = Array.from(new Set([...previous, ...extensions]));
    });
  });

  return mergedMap;
};

const getUniqueFileFormatExtensions = (map: Record<string, string[]>) =>
  Array.from(
    new Set(
      Object.values(map)
        .flat()
        .map(extension => normalizeFileFormatExtension(extension))
        .filter(Boolean)
    )
  );

const inferInputType = (
  key: string,
  value: unknown
): ValidationFieldInputType => {
  const overriddenType = VALIDATION_KEY_INPUT_TYPE_OVERRIDES[key];
  if (overriddenType) return overriddenType;

  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (Array.isArray(value)) return 'array';
  if (isObject(value)) return 'object';

  return 'string';
};

const toEditorValue = (value: unknown): ValidationInputValue => {
  if (value === undefined || value === null) return '';

  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.join(', ');
  if (isObject(value)) return JSON.stringify(value, null, 2);

  return getStringValue(value);
};

const parseArrayInput = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return [];

  return trimmed
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
};

const parseObjectInput = (value: string, fallback: unknown) => {
  const trimmed = value.trim();
  if (!trimmed) return {};

  try {
    return JSON.parse(trimmed);
  } catch {
    return fallback;
  }
};

const parseValidationValueByInputType = (
  inputType: ValidationFieldInputType,
  baseValue: unknown,
  formValue: ValidationInputValue | undefined
) => {
  if (inputType === 'boolean') {
    if (typeof formValue === 'boolean') {
      return formValue;
    }

    return typeof baseValue === 'boolean' ? baseValue : undefined;
  }

  if (formValue === undefined) {
    return baseValue;
  }

  if (inputType === 'number') {
    if (typeof formValue !== 'string' || !formValue.trim()) return undefined;

    const parsed = Number(formValue);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  if (inputType === 'array') {
    if (typeof formValue !== 'string') return [];
    return parseArrayInput(formValue);
  }

  if (inputType === 'object') {
    if (typeof formValue !== 'string') return baseValue;
    return parseObjectInput(formValue, baseValue);
  }

  return typeof formValue === 'string' ? formValue.trim() : String(formValue);
};

const isEmptyValidationValue = (
  value: unknown,
  inputType: ValidationFieldInputType
) => {
  if (inputType === 'boolean') return false;
  if (value === undefined || value === null) return true;

  if (typeof value === 'string') return !value.trim();
  if (Array.isArray(value)) return value.length === 0;
  if (isObject(value)) return Object.keys(value).length === 0;

  return false;
};

export const isDependentField = (formField: FormFieldDefinition) => {
  const validations = toRecord(formField.params?.validations);
  const kind = String(validations?.kind ?? '');
  const dependencyPath = getExternalDependencyPath(formField);

  return (
    DEPENDENT_VALIDATION_KINDS.has(kind) ||
    'dependantField' in validations ||
    'dependentField' in validations ||
    Boolean(dependencyPath)
  );
};

export const getDependentFieldsByParentPath = (
  formFields: FormFieldDefinition[]
) => {
  const dependentFieldsByPath = new Map<string, Set<string>>();

  formFields.forEach(field => {
    const parentPath = getExternalDependencyPath(field);
    if (!parentPath) return;

    const dependentFieldName = getStringValue(field.name).trim();
    if (!dependentFieldName) return;

    const existing = dependentFieldsByPath.get(parentPath);
    if (existing) {
      existing.add(dependentFieldName);
      return;
    }

    dependentFieldsByPath.set(parentPath, new Set([dependentFieldName]));
  });

  return dependentFieldsByPath;
};

export const getDependentParentFieldByFieldName = (
  formFields: FormFieldDefinition[]
) => {
  const parentFieldByCaseDataPath = new Map<string, string>();
  const dependentParentFieldByName = new Map<string, string>();

  formFields.forEach(field => {
    const caseDataMainPath = getCaseDataMainPath(field);
    const fieldName = getStringValue(field.name).trim();
    if (!caseDataMainPath || !fieldName) return;

    parentFieldByCaseDataPath.set(
      normalizeFieldKey(caseDataMainPath),
      fieldName
    );
  });

  formFields.forEach(field => {
    const parentPath = getExternalDependencyPath(field);
    const dependentFieldName = getStringValue(field.name).trim();
    if (!parentPath || !dependentFieldName) return;

    const parentFieldName = parentFieldByCaseDataPath.get(
      normalizeFieldKey(parentPath)
    );
    if (!parentFieldName) return;

    dependentParentFieldByName.set(
      normalizeFieldKey(dependentFieldName),
      parentFieldName
    );
  });

  return dependentParentFieldByName;
};

export const mapFormFieldToRow = (
  formField: FormFieldDefinition,
  index: number
): ArticleTypeConfigField => ({
  id: `${formField.name}-${index}`,
  field: formField.name,
  kind: formField.params.validations.kind ?? 'string',
  required: Boolean(formField.params.validations.required),
  validations: formField.params.validations,
  labels: formField.params.labels
});

export const mapFormFieldToSourceOption = (
  formField: FormFieldDefinition,
  index: number
): SourceFieldOption => ({
  id: `${formField.name}-${index}`,
  field: formField.name,
  kind: formField.params.validations.kind ?? 'string',
  required: Boolean(formField.params.validations.required),
  validations: formField.params.validations,
  labels: formField.params.labels,
  source: formField
});

export const getValidationFieldDefinitions = (
  validations: unknown
): ValidationFieldDefinition[] => {
  const validationRecord = toRecord(validations);
  const kind = getValidationKind(validationRecord);
  const baseDefinitions = VALIDATION_FIELDS_BY_KIND[kind] ?? [];
  const definitionMap = new Map<string, ValidationFieldDefinition>();

  baseDefinitions.forEach(definition => {
    definitionMap.set(definition.key, {
      key: definition.key,
      inputType: definition.inputType,
      value: validationRecord[definition.key]
    });
  });

  Object.entries(validationRecord).forEach(([key, value]) => {
    if (EXCLUDED_VALIDATION_EDITOR_KEYS.has(key)) return;
    if (value === undefined || value === null) return;

    const existing = definitionMap.get(key);
    if (existing) {
      definitionMap.set(key, { ...existing, value });
      return;
    }

    definitionMap.set(key, {
      key,
      inputType: inferInputType(key, value),
      value
    });
  });

  return Array.from(definitionMap.values());
};

export const getAllowedFileFormatOptions = (validations: unknown) => {
  const validationRecord = toRecord(validations);
  const existingFormatMap = toAllowedFileFormatMap(
    validationRecord.allowedFileFormats
  );
  const existingOptions = getUniqueFileFormatExtensions(existingFormatMap);

  const kind = getValidationKind(validationRecord);
  if (!existingOptions.length && !KINDS_WITH_ALLOWED_FILE_FORMATS.has(kind))
    return [];

  const mergedMap = mergeAllowedFileFormatMaps(
    DEFAULT_ALLOWED_FILE_FORMAT_MAP,
    existingFormatMap
  );

  return getUniqueFileFormatExtensions(mergedMap);
};

export const getSelectedAllowedFileFormatKeys = (validations: unknown) =>
  getUniqueFileFormatExtensions(
    toAllowedFileFormatMap(toRecord(validations).allowedFileFormats)
  );

export const getLabelCharacterLimits = (validations: unknown) => {
  const validationRecord = toRecord(validations);
  const subtitleCharacterLimit = toPositiveNumber(validationRecord.maxLength);
  const helpTextCharacterLimit =
    toPositiveNumber(validationRecord.textMaxLength) || subtitleCharacterLimit;

  return {
    subtitleCharacterLimit,
    helpTextCharacterLimit
  };
};

export const toFieldFormValues = (
  source: Pick<
    ArticleTypeConfigField,
    'field' | 'kind' | 'required' | 'validations' | 'labels'
  >
): FieldFormValues => {
  const validations = toRecord(source.validations);
  const labels = toRecord(source.labels);
  const sourceOption = source as Partial<SourceFieldOption>;
  const sourceDefinition = sourceOption.source;
  const labelHelpText = getLabelHelpTextValue({
    labels,
    source: sourceDefinition
  });
  const validationDefinitions = getValidationFieldDefinitions(validations);
  const dependentFieldFromReference = getStringValue(
    sourceOption.dependentFieldFromReference
  );
  const dependentFieldFromValidation = getStringValue(
    validations.dependantField ?? validations.dependentField
  );
  const dependentFieldFromCaseData =
    getDependentFieldFromCaseData(sourceDefinition);
  const validationValues = validationDefinitions.reduce<
    Record<string, ValidationInputValue>
  >((acc, definition) => {
    const { key, value } = definition;
    if (key === 'allowedFileFormats' || NON_EDITABLE_VALIDATION_KEYS.has(key))
      return acc;
    acc[key] = toEditorValue(value);
    return acc;
  }, {});

  return {
    sourceFieldName: source.field,
    field: source.field,
    kind: getStringValue(validations.kind || source.kind || 'string'),
    required: Boolean(validations.required ?? source.required),
    dependentField:
      dependentFieldFromReference ||
      dependentFieldFromValidation ||
      dependentFieldFromCaseData,
    validationValues,
    allowedFileFormats: getSelectedAllowedFileFormatKeys(validations),
    labelTitle: getStringValue(labels.main),
    labelSubtitle: getStringValue(labels.subtitle),
    showHelpText: Boolean(labelHelpText),
    labelHelpText,
    labelContent: getStringValue(labels.content)
  };
};

export const toFieldEditorSubmit = ({
  values,
  baseValidations,
  baseLabels
}: {
  values: FieldFormValues;
  baseValidations: unknown;
  baseLabels: unknown;
}): FieldEditorSubmit => {
  const validations = { ...toRecord(baseValidations) };
  const labels = { ...toRecord(baseLabels) };
  const shouldPreserveTitleAndSubtitle = shouldUseContentLabelMode(baseLabels);
  const baseValidationRecord = toRecord(baseValidations);
  const validationDefinitions = getValidationFieldDefinitions({
    ...baseValidationRecord,
    kind: values.kind
  });

  validations.kind = values.kind;
  validations.required = Boolean(values.required);

  const dependentField = values.dependentField.trim();
  if ('dependantField' in validations || 'dependentField' in validations) {
    if (dependentField) {
      if ('dependantField' in validations) {
        validations.dependantField = dependentField;
      } else {
        validations.dependentField = dependentField;
      }
    }
  }

  validationDefinitions.forEach(({ key, inputType, value: baseValue }) => {
    if (key === 'allowedFileFormats' || NON_EDITABLE_VALIDATION_KEYS.has(key))
      return;

    const nextValue = parseValidationValueByInputType(
      inputType,
      baseValue,
      values.validationValues?.[key]
    );

    if (isEmptyValidationValue(nextValue, inputType)) {
      delete validations[key];
      return;
    }

    validations[key] = nextValue;
  });

  const selectedFormatKeys = values.allowedFileFormats || [];
  const isAllowedFileFormatSupported =
    'allowedFileFormats' in baseValidationRecord ||
    KINDS_WITH_ALLOWED_FILE_FORMATS.has(values.kind);

  if (isAllowedFileFormatSupported) {
    if (!selectedFormatKeys.length) {
      delete validations.allowedFileFormats;
    } else {
      const baseAllowedFileFormatMap = toAllowedFileFormatMap(
        baseValidationRecord.allowedFileFormats
      );
      const mergedAllowedFileFormatMap = mergeAllowedFileFormatMaps(
        DEFAULT_ALLOWED_FILE_FORMAT_MAP,
        baseAllowedFileFormatMap
      );
      const selectedExtensions = new Set(
        selectedFormatKeys.map(extension =>
          normalizeFileFormatExtension(extension)
        )
      );

      const allowedFileFormats: JsonRecord = {};

      Object.entries(mergedAllowedFileFormatMap).forEach(
        ([mimeType, extensions]) => {
          const selectedByMimeType = extensions.filter(extension =>
            selectedExtensions.has(normalizeFileFormatExtension(extension))
          );

          if (!selectedByMimeType.length) return;
          allowedFileFormats[mimeType] = selectedByMimeType;
        }
      );

      if (!Object.keys(allowedFileFormats).length) {
        delete validations.allowedFileFormats;
      } else {
        validations.allowedFileFormats = allowedFileFormats;
      }
    }
  }

  if (!shouldPreserveTitleAndSubtitle) {
    labels.main = values.labelTitle.trim();
    const subtitle = values.labelSubtitle.trim();
    if (subtitle) labels.subtitle = subtitle;
    else delete labels.subtitle;
  }

  labels.helpText = values.showHelpText ? values.labelHelpText.trim() : '';

  labels.content = values.labelContent?.trim() || '';

  return {
    field: values.field.trim(),
    kind: values.kind,
    required: Boolean(values.required),
    validations,
    labels
  };
};

export const dedupeSourceFieldOptions = (options: SourceFieldOption[]) => {
  const seen = new Set<string>();

  return options.filter(option => {
    const normalized = option.field.trim().toLowerCase();
    if (!normalized || seen.has(normalized)) return false;

    const source = option.source;

    const hasExternalDependency = Boolean(
      source?.params &&
        typeof source.params === 'object' &&
        (source.params as any).externalFieldDependency?.path
    );

    const caseDataFields =
      source && typeof source === 'object'
        ? (source as any).caseDataFields
        : undefined;

    const dependsOn = caseDataFields?.dependsOn?.toLowerCase?.();
    const isExistsDependency = dependsOn === 'exists' || dependsOn === 'exist';

    // ❌ remove external dependency (except exists)
    if (hasExternalDependency && !isExistsDependency) {
      return false;
    }

    seen.add(normalized);
    return true;
  });
};

const formatLeafValue = (value: unknown) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string' || typeof value === 'number')
    return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const formatAllowedFileFormatsValue = (value: unknown) => {
  const formatMap = toAllowedFileFormatMap(value);
  const extensions = getUniqueFileFormatExtensions(formatMap);
  return extensions.join(', ') || '-';
};

const formatCompactJsonValue = (value: unknown) => {
  if (value === null || value === undefined) return '-';

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '-';

    try {
      return JSON.stringify(JSON.parse(trimmed));
    } catch {
      return trimmed;
    }
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
};

export const flattenObjectEntries = (
  value: unknown,
  parentKey = ''
): Array<{ keyPath: string; value: string }> => {
  if (Array.isArray(value)) {
    return [
      {
        keyPath: parentKey || 'items',
        value: value.map(item => formatLeafValue(item)).join(', ')
      }
    ];
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);

    if (!entries.length) {
      return [{ keyPath: parentKey || 'value', value: '{}' }];
    }

    return entries.flatMap(([key, nextValue]) => {
      const nextKeyPath = parentKey ? `${parentKey}.${key}` : key;
      const shouldKeepParentOnly =
        NON_FLATTENED_PARENT_KEYS.has(key) &&
        nextValue &&
        typeof nextValue === 'object' &&
        !Array.isArray(nextValue);

      if (shouldKeepParentOnly) {
        const cellValue = (() => {
          if (key === 'allowedFileFormats') {
            return formatAllowedFileFormatsValue(nextValue);
          }

          if (key === 'items') {
            return formatCompactJsonValue(nextValue);
          }

          return Object.keys(nextValue as JsonRecord).join(', ') || '{}';
        })();

        return [
          {
            keyPath: nextKeyPath,
            value: cellValue
          }
        ];
      }

      if (
        nextValue &&
        typeof nextValue === 'object' &&
        !Array.isArray(nextValue)
      ) {
        return flattenObjectEntries(nextValue, nextKeyPath);
      }

      return [
        {
          keyPath: nextKeyPath,
          value: formatLeafValue(nextValue)
        }
      ];
    });
  }

  return [{ keyPath: parentKey || 'value', value: formatLeafValue(value) }];
};

// Split before capital letters, but keep groups like "MB" together
export const normalizeText = (text: string) =>
  text.replace(/([a-z0-9])([A-Z])|([A-Z]+)([A-Z][a-z])/g, '$1$3 $2$4').trim();

export const capitalizeFirstLetter = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1);

export const formatKeyPath = (keyPath: string) =>
  keyPath
    .split('.')
    .filter(Boolean)
    .map(segment => capitalizeFirstLetter(normalizeText(segment)))
    .join(' > ');

// Focus issue is managed outside of the article type field configuration
const FOCUS_ISSUE_COMPONENT_NAME = 'focusselect';

export const isFocusIssueComponent = (componentName?: string) => {
  if (!componentName) {
    return false;
  }

  const cleanedComponentName = componentName.split(':').pop() || componentName;

  return (
    cleanedComponentName.trim().toLowerCase() === FOCUS_ISSUE_COMPONENT_NAME
  );
};

export const getDisplayFieldTypeLabel = ({
  fieldName,
  kind,
  componentName
}: {
  fieldName?: string;
  kind?: string;
  componentName?: string;
}) => {
  const fallbackType = kind || '-';

  if (!fieldName || !componentName) {
    return fallbackType;
  }

  const cleanedComponentName = componentName.split(':').pop() || componentName;
  const normalizedFieldName = fieldName.trim().toLowerCase();
  const normalizedComponentName = cleanedComponentName.trim().toLowerCase();

  if (
    normalizedFieldName &&
    normalizedComponentName.includes(normalizedFieldName)
  ) {
    return fallbackType;
  }

  return cleanedComponentName || fallbackType;
};
